import JSZip from 'jszip';
import { useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { env } from '../env';

const BASE_URL = env.apiUrl;

export interface ZipPostData {
  title: string;
  imageUrl: string;
  logoUrl: string;
  shortDescription: string;
  domain: string;
  tags: string[];
}

interface Props {
  onImport: (data: ZipPostData) => void;
}

function extOf(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() ?? 'jpg';
}

function mimeForExt(ext: string): string {
  const map: Record<string, string> = {
    svg: 'image/svg+xml',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    webp: 'image/webp',
    gif: 'image/gif',
  };
  return map[ext] ?? 'application/octet-stream';
}

async function uploadFile(blob: Blob, filename: string, endpoint: string): Promise<string> {
  const formData = new FormData();
  formData.append('image', blob, filename);
  const res = await fetch(`${BASE_URL}${endpoint}`, { method: 'POST', body: formData });
  if (!res.ok) throw new Error(`Upload failed for ${filename}`);
  const data = await res.json() as { url: string };
  return data.url;
}

export function ZipImportButton({ onImport }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setError(null);
    setImporting(true);

    try {
      const zip = await JSZip.loadAsync(file);
      const entries = Object.values(zip.files).filter(f => !f.dir);

      // Find JSON file
      const jsonEntry = entries.find(f => f.name.toLowerCase().endsWith('.json'));
      if (!jsonEntry) throw new Error('No JSON file found in zip');
      const jsonText = await jsonEntry.async('text');
      const postData = JSON.parse(jsonText) as Partial<ZipPostData>;

      // Find image file — base name (no ext) ends with "image"
      const imageEntry = entries.find(f => {
        const base = f.name.split('/').pop() ?? '';
        const nameNoExt = base.replace(/\.[^.]+$/, '');
        return nameNoExt.toLowerCase().endsWith('image');
      });

      // Find logo file — base name (no ext) ends with "logo"
      const logoEntry = entries.find(f => {
        const base = f.name.split('/').pop() ?? '';
        const nameNoExt = base.replace(/\.[^.]+$/, '');
        return nameNoExt.toLowerCase().endsWith('logo');
      });

      let imageUrl = postData.imageUrl ?? '';
      let logoUrl = postData.logoUrl ?? '';

      if (imageEntry) {
        const raw = await imageEntry.async('arraybuffer');
        const base = imageEntry.name.split('/').pop() ?? 'image';
        const ext = extOf(base);
        const blob = new Blob([raw], { type: mimeForExt(ext) });
        imageUrl = await uploadFile(blob, `blog-image.${ext}`, '/api/images/upload');
      }

      if (logoEntry) {
        const raw = await logoEntry.async('arraybuffer');
        const base = logoEntry.name.split('/').pop() ?? 'logo';
        const ext = extOf(base);
        const blob = new Blob([raw], { type: mimeForExt(ext) });
        logoUrl = await uploadFile(blob, `blog-logo.${ext}`, '/api/images/upload?folder=blog-logos');
      }

      onImport({
        title: postData.title ?? '',
        imageUrl,
        logoUrl,
        shortDescription: postData.shortDescription ?? '',
        domain: postData.domain ?? 'General',
        tags: postData.tags ?? [],
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      setError(msg === 'No JSON file found in zip' ? msg : 'Import failed. Check the zip file and try again.');
    } finally {
      setImporting(false);
    }
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type='file'
        accept='.zip'
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <Box>
        <Button
          variant='outlined'
          color='secondary'
          onClick={() => fileInputRef.current?.click()}
          disabled={importing}
          startIcon={importing ? <CircularProgress size={14} color='inherit' /> : undefined}
        >
          {importing ? 'Importing…' : 'Import ZIP'}
        </Button>
        {error && (
          <Typography variant='caption' color='error' display='block' sx={{ mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </Box>
    </>
  );
}
